import { AlertTriangle, RefreshCw, SearchX } from "lucide-react";

import type { WalletTransactionEntity } from "@/modules/dashboard/cards/types/wallets-api.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardWalletMovementsFilters as Filters } from "../../types/cardWallets.types";
import { hasActiveMovementFilters, MOVEMENTS_PAGE_SIZE } from "../../utils/cardWalletMovements";
import { CardWalletMovementsCards } from "./CardWalletMovementsCards";
import { CardWalletMovementsFilters } from "./CardWalletMovementsFilters";
import { CardWalletMovementsTable } from "./CardWalletMovementsTable";

export interface CardWalletMovementsProps {
  movements: WalletTransactionEntity[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onClearFilters: () => void;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  currentUserId: string | null;
  highlightedProviderTxId: string | null;
}

/**
 * The wallet's statement: the filters, the two faces of the list, and the paginator.
 *
 * "No results" and "the request failed" are never conflated — they lead an operator to opposite actions — and
 * the filtered-empty state is distinct from the never-had-movements one for the same reason.
 */
export function CardWalletMovements({
  movements,
  total,
  page,
  onPageChange,
  filters,
  onFiltersChange,
  onClearFilters,
  isLoading,
  error,
  onRetry,
  currentUserId,
  highlightedProviderTxId,
}: CardWalletMovementsProps) {
  const { t } = useTranslations("cardWallets");
  const filtered = hasActiveMovementFilters(filters);
  const showsFilteredEmpty = !isLoading && !error && movements.length === 0 && filtered;

  return (
    <section
      aria-labelledby="card-wallet-movements-title"
      className={cn(colocaSurfaceVariants({ variant: "panel", padding: "none" }), "overflow-hidden")}
    >
      <div className="flex flex-col gap-3 border-b border-border p-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <h2
          id="card-wallet-movements-title"
          className="flex items-center gap-2 text-base font-semibold tracking-tight"
        >
          {t("history_title")}
          <ColocaBadge variant="secondary">{total}</ColocaBadge>
        </h2>
        <CardWalletMovementsFilters filters={filters} onChange={onFiltersChange} onClear={onClearFilters} />
      </div>

      {error ? (
        <div className="p-4">
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            text={error || t("history_error")}
            actions={
              <ColocaButton type="button" variant="default" size="sm" icon={RefreshCw} onClick={onRetry}>
                {t("action_retry")}
              </ColocaButton>
            }
          />
        </div>
      ) : showsFilteredEmpty ? (
        <div className="p-4">
          <ColocaEmpty surface="card" icon={SearchX} title={t("empty_filtered_title")}>
            <ColocaButton type="button" variant="default" onClick={onClearFilters}>
              {t("filter_clear")}
            </ColocaButton>
          </ColocaEmpty>
        </div>
      ) : (
        <>
          <div className="hidden xl:block">
            <CardWalletMovementsTable
              movements={movements}
              isLoading={isLoading}
              currentUserId={currentUserId}
              highlightedProviderTxId={highlightedProviderTxId}
            />
          </div>
          <div className="xl:hidden">
            <CardWalletMovementsCards
              movements={movements}
              isLoading={isLoading}
              currentUserId={currentUserId}
              highlightedProviderTxId={highlightedProviderTxId}
            />
          </div>
        </>
      )}

      {!error && total > MOVEMENTS_PAGE_SIZE && (
        <div className="border-t border-border px-4 py-3">
          <ColocaPagination
            page={page}
            perPage={MOVEMENTS_PAGE_SIZE}
            total={total}
            size="sm"
            withSeparator={false}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </section>
  );
}
